// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import {autorun} from 'mobx';
import {useCallback, useSyncExternalStore} from 'react';

type QuickReactionSnapshot = ReadonlyArray<FlatEmoji>;
type EmojiState = typeof import('@app/features/emoji/state/Emoji')['default'];
type EmojiPickerState = typeof import('@app/features/emoji/state/EmojiPicker')['default'];
type PermissionState = typeof import('@app/features/permissions/state/Permission')['default'];
type ComponentDispatchState = typeof import('@app/features/platform/utils/ComponentBus')['ComponentDispatch'];

interface QuickReactionDependencies {
	Emoji: EmojiState;
	EmojiPicker: EmojiPickerState;
	Permission: PermissionState;
	ComponentDispatch: ComponentDispatchState;
}

interface CacheEntry {
	key: string;
	channel: Channel | null;
	count: number;
	snapshot: QuickReactionSnapshot | null;
	updatedAt: number;
	stale: boolean;
	pending: boolean;
}

const EMPTY_QUICK_REACTIONS: QuickReactionSnapshot = Object.freeze([]);
const DEFAULT_FALLBACK_POOL_SIZE = 16;
const QUICK_REACTION_CACHE_TTL_MS = 30_000;
const MAX_CACHE_ENTRIES = 80;
const WARMED_REACTION_COUNTS = [3, 4, 5, 16] as const;

let dependenciesPromise: Promise<QuickReactionDependencies> | null = null;

const normalizeCount = (count: number): number => Math.max(0, Math.floor(count));

function loadQuickReactionDependencies(): Promise<QuickReactionDependencies> {
	if (!dependenciesPromise) {
		dependenciesPromise = Promise.all([
			import('@app/features/emoji/state/Emoji'),
			import('@app/features/emoji/state/EmojiPicker'),
			import('@app/features/permissions/state/Permission'),
			import('@app/features/platform/utils/ComponentBus'),
		]).then(([emojiModule, emojiPickerModule, permissionModule, componentBusModule]) => ({
			Emoji: emojiModule.default,
			EmojiPicker: emojiPickerModule.default,
			Permission: permissionModule.default,
			ComponentDispatch: componentBusModule.ComponentDispatch,
		}));
	}
	return dependenciesPromise;
}

function getEmojiKey(emoji: FlatEmoji): string {
	if (emoji.id) {
		return `custom:${emoji.guildId ?? ''}:${emoji.id}`;
	}
	return `unicode:${emoji.uniqueName}`;
}

function snapshotsEqual(a: QuickReactionSnapshot | null, b: QuickReactionSnapshot): boolean {
	if (!a || a.length !== b.length) {
		return false;
	}
	for (let i = 0; i < a.length; i++) {
		if (getEmojiKey(a[i]) !== getEmojiKey(b[i])) {
			return false;
		}
	}
	return true;
}

function getCacheKey(channel: Channel | null, count: number): string {
	if (!channel) {
		return `global:${count}`;
	}
	return `${channel.guildId ?? 'dm'}:${channel.id}:${count}`;
}

function scheduleBackgroundTask(callback: () => void): void {
	if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
		window.requestIdleCallback(callback, {timeout: 50});
		return;
	}
	if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
		window.requestAnimationFrame(() => {
			setTimeout(callback, 0);
		});
		return;
	}
	setTimeout(callback, 0);
}

class QuickReactionStore {
	private readonly cache = new Map<string, CacheEntry>();
	private readonly listeners = new Map<string, Set<() => void>>();
	private readonly defaultSnapshots = new Map<number, QuickReactionSnapshot>();
	private dependencies: QuickReactionDependencies | null = null;
	private initialized = false;

	private ensureInitialized(): void {
		if (this.initialized) {
			return;
		}
		this.initialized = true;
		void this.ensureDependencies().then((dependencies) => {
			this.warmGlobalSnapshots();
			autorun(() => {
				const usage = dependencies.EmojiPicker.emojiUsage;
				for (const key in usage) {
					const entry = usage[key];
					entry?.count;
					entry?.lastUsed;
				}
				this.invalidateAll();
			});
			dependencies.ComponentDispatch.subscribe('EMOJI_PICKER_RERENDER', () => {
				this.defaultSnapshots.clear();
				this.warmDefaultSnapshots();
				this.invalidateAll();
			});
			dependencies.Permission.subscribe(() => {
				this.invalidateAll();
			});
		});
	}

	private async ensureDependencies(): Promise<QuickReactionDependencies> {
		if (this.dependencies) {
			return this.dependencies;
		}
		this.dependencies = await loadQuickReactionDependencies();
		return this.dependencies;
	}

	subscribe(channel: Channel | null, count: number, listener: () => void): () => void {
		this.ensureInitialized();
		const normalizedCount = normalizeCount(count);
		if (normalizedCount === 0) {
			return () => undefined;
		}
		const key = getCacheKey(channel, normalizedCount);
		let listeners = this.listeners.get(key);
		if (!listeners) {
			listeners = new Set();
			this.listeners.set(key, listeners);
		}
		listeners.add(listener);
		const entry = this.getOrCreateEntry(channel, normalizedCount);
		this.scheduleRefreshIfNeeded(entry);
		this.warmRelatedEntries(channel, normalizedCount);
		return () => {
			listeners?.delete(listener);
			if (listeners?.size === 0) {
				this.listeners.delete(key);
			}
		};
	}

	getSnapshot(channel: Channel | null, count: number): QuickReactionSnapshot {
		this.ensureInitialized();
		const normalizedCount = normalizeCount(count);
		if (normalizedCount === 0) {
			return EMPTY_QUICK_REACTIONS;
		}
		const entry = this.cache.get(getCacheKey(channel, normalizedCount));
		return entry?.snapshot ?? this.getDefaultSnapshot(normalizedCount);
	}

	getDefaultSnapshot(count: number): QuickReactionSnapshot {
		this.ensureInitialized();
		const normalizedCount = normalizeCount(count);
		if (normalizedCount === 0) {
			return EMPTY_QUICK_REACTIONS;
		}
		const cached = this.defaultSnapshots.get(normalizedCount);
		if (cached) {
			return cached;
		}
		const dependencies = this.dependencies;
		if (!dependencies) {
			return EMPTY_QUICK_REACTIONS;
		}
		const defaultNames = dependencies.EmojiPicker.getDefaultQuickEmojiNames(
			Math.min(normalizedCount, DEFAULT_FALLBACK_POOL_SIZE),
		);
		const defaults: Array<FlatEmoji> = [];
		const seenKeys = new Set<string>();
		for (const emojiName of defaultNames) {
			const emojiKey = `unicode:${emojiName}`;
			const emoji = dependencies.Emoji.getEmojiByUsageKey(emojiKey);
			if (!emoji || seenKeys.has(emojiKey)) {
				continue;
			}
			seenKeys.add(emojiKey);
			defaults.push(emoji);
			if (defaults.length >= normalizedCount) {
				break;
			}
		}
		const snapshot = Object.freeze(defaults);
		this.defaultSnapshots.set(normalizedCount, snapshot);
		return snapshot;
	}

	private getOrCreateEntry(channel: Channel | null, count: number): CacheEntry {
		const key = getCacheKey(channel, count);
		const existing = this.cache.get(key);
		if (existing) {
			existing.channel = channel;
			existing.count = count;
			this.cache.delete(key);
			this.cache.set(key, existing);
			return existing;
		}
		const entry: CacheEntry = {
			key,
			channel,
			count,
			snapshot: null,
			updatedAt: 0,
			stale: true,
			pending: false,
		};
		this.cache.set(key, entry);
		this.pruneCache();
		return entry;
	}

	private warmDefaultSnapshots(): void {
		for (const count of WARMED_REACTION_COUNTS) {
			this.getDefaultSnapshot(count);
		}
	}

	private warmGlobalSnapshots(): void {
		this.warmDefaultSnapshots();
		for (const count of WARMED_REACTION_COUNTS) {
			this.scheduleRefreshIfNeeded(this.getOrCreateEntry(null, count));
		}
	}

	private warmRelatedEntries(channel: Channel | null, requestedCount: number): void {
		for (const count of WARMED_REACTION_COUNTS) {
			if (count <= requestedCount) {
				continue;
			}
			this.scheduleRefreshIfNeeded(this.getOrCreateEntry(channel, count));
		}
	}

	private invalidateAll(): void {
		for (const entry of this.cache.values()) {
			entry.stale = true;
		}
		for (const [key, listeners] of this.listeners.entries()) {
			if (listeners.size === 0) {
				continue;
			}
			const entry = this.cache.get(key);
			if (entry) {
				this.scheduleRefresh(entry);
			}
		}
	}

	private scheduleRefresh(entry: CacheEntry): void {
		if (entry.pending) {
			return;
		}
		entry.pending = true;
		scheduleBackgroundTask(() => {
			void this.refreshAsync(entry);
		});
	}

	private scheduleRefreshIfNeeded(entry: CacheEntry): void {
		if (this.shouldRefresh(entry)) {
			this.scheduleRefresh(entry);
		}
	}

	private shouldRefresh(entry: CacheEntry): boolean {
		return !entry.snapshot || entry.stale || Date.now() - entry.updatedAt > QUICK_REACTION_CACHE_TTL_MS;
	}

	private async refreshAsync(entry: CacheEntry): Promise<void> {
		try {
			const dependencies = await this.ensureDependencies();
			this.refresh(entry, dependencies);
		} finally {
			entry.pending = false;
		}
	}

	private refresh(entry: CacheEntry, dependencies: QuickReactionDependencies): void {
		let next = dependencies.Emoji.getQuickReactionEmojis(entry.channel, entry.count);
		if (next.length === 0) {
			const fallback =
				entry.snapshot && entry.snapshot.length > 0 ? entry.snapshot : this.getDefaultSnapshot(entry.count);
			next = fallback.slice();
		}
		const snapshot = Object.freeze(next.slice(0, entry.count));
		entry.stale = false;
		entry.updatedAt = Date.now();
		if (snapshotsEqual(entry.snapshot, snapshot)) {
			entry.snapshot = snapshot;
			return;
		}
		entry.snapshot = snapshot;
		this.notify(entry.key);
	}

	private notify(key: string): void {
		const listeners = this.listeners.get(key);
		if (!listeners) {
			return;
		}
		for (const listener of listeners) {
			listener();
		}
	}

	private pruneCache(): void {
		let protectedEntriesScanned = 0;
		while (this.cache.size > MAX_CACHE_ENTRIES) {
			const oldestKey = this.cache.keys().next().value;
			if (oldestKey === undefined) {
				return;
			}
			const oldestEntry = this.cache.get(oldestKey);
			if (!oldestEntry) {
				this.cache.delete(oldestKey);
				continue;
			}
			if (this.listeners.has(oldestKey)) {
				this.cache.delete(oldestKey);
				this.cache.set(oldestKey, oldestEntry);
				protectedEntriesScanned += 1;
				if (protectedEntriesScanned >= this.cache.size) {
					return;
				}
				continue;
			}
			this.cache.delete(oldestKey);
			protectedEntriesScanned = 0;
		}
	}
}

let quickReactions: QuickReactionStore | null = null;

function getQuickReactions(): QuickReactionStore {
	if (!quickReactions) {
		quickReactions = new QuickReactionStore();
	}
	return quickReactions;
}

export function useQuickReactionEmojis(
	channel: Channel | null,
	count: number,
	enabled: boolean,
): QuickReactionSnapshot {
	const subscribe = useCallback(
		(listener: () => void) => {
			if (!enabled) {
				return () => undefined;
			}
			return getQuickReactions().subscribe(channel, count, listener);
		},
		[channel, count, enabled],
	);
	const getSnapshot = useCallback(() => {
		if (!enabled) {
			return EMPTY_QUICK_REACTIONS;
		}
		return getQuickReactions().getSnapshot(channel, count);
	}, [channel, count, enabled]);
	const getServerSnapshot = useCallback(() => {
		if (!enabled) {
			return EMPTY_QUICK_REACTIONS;
		}
		return getQuickReactions().getDefaultSnapshot(count);
	}, [count, enabled]);
	return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
